import {computed, ref, watch} from "vue";
import dayjs, {Dayjs} from "dayjs";
import {teamPlanningApi} from "@/container";
import useOrganizationId from "@/core/composables/useOrganizationId";
import useLatestFetch from "@/core/composables/useLatestFetch";
import {TeamAbsence} from "@/core/interfaces/TeamPlanning";
import {AbsenceStoreRequest, AbsenceUpdateRequest} from "@/core/http/resources/TeamPlanningResource";

/** Rows per archive page. The archive is the only listing without a bound. */
export const ARCHIVE_PAGE_SIZE = 10;

/**
 * The team's absences, split the way the screen reads them: the ones under
 * way or still to come, and the archive of those already over.
 *
 * The split is a date, not a flag — an absence archives itself the day after
 * it ends — and the pivot is read on each fetch so a session left open across
 * midnight re-partitions on its own.
 *
 * The timeline reads a third set, bound to the month it shows: the server
 * filters on overlap, so an absence spanning the month's edges comes back
 * whole and the timeline can crop it itself.
 *
 * `onWritten` runs after every successful write: the planning grid masks the
 * days an absence covers, so it goes stale the moment one is saved.
 */
const useTeamAbsences = (onWritten?: () => void | Promise<void>) => {
    const organizationId = useOrganizationId();

    const active = ref<TeamAbsence[]>([]);
    const activeLoaded = ref<boolean>(false);
    const {loading: activeLoading, failed: activeFailed, run: runActive} = useLatestFetch();

    const archive = ref<TeamAbsence[]>([]);
    const archiveTotal = ref<number>(0);
    const archiveLastPage = ref<number>(1);
    const archivePage = ref<number>(1);
    const archiveLoaded = ref<boolean>(false);
    const {loading: archiveLoading, failed: archiveFailed, run: runArchive} = useLatestFetch();

    const month = ref<TeamAbsence[]>([]);
    const monthAnchor = ref<Dayjs>(dayjs().startOf('month'));
    const monthLoaded = ref<boolean>(false);
    const {loading: monthLoading, failed: monthFailed, run: runMonth} = useLatestFetch();

    const today = (): string => dayjs().format('YYYY-MM-DD');

    const refreshActive = async (): Promise<void> => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId) {
            return;
        }

        await runActive(async isLatest => {
            const next = await teamPlanningApi().absences(currentOrganizationId, {from: today()});

            if (isLatest()) {
                active.value = next;
                activeLoaded.value = true;
            }
        });
    };

    const refreshArchive = async (): Promise<void> => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId) {
            return;
        }

        await runArchive(async isLatest => {
            const page = await teamPlanningApi().absencesPage(currentOrganizationId, {
                ends_before: today(),
                limit: ARCHIVE_PAGE_SIZE,
                page: archivePage.value,
                sort: 'desc'
            });

            if (isLatest()) {
                archive.value = page.items;
                archiveTotal.value = page.total;
                archiveLastPage.value = page.lastPage;
                archiveLoaded.value = true;
            }
        });

        // Deleting the last row of the last page leaves the reader past the
        // end, on a page the server answers empty. Clamping settles in one
        // extra fetch, a failed one keeping the page count it already had.
        if (archivePage.value > archiveLastPage.value) {
            archivePage.value = archiveLastPage.value;

            await refreshArchive();
        }
    };

    const refreshMonth = async (): Promise<void> => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId) {
            return;
        }

        const anchor = monthAnchor.value;

        await runMonth(async isLatest => {
            const next = await teamPlanningApi().absences(currentOrganizationId, {
                from: anchor.startOf('month').format('YYYY-MM-DD'),
                to: anchor.endOf('month').format('YYYY-MM-DD')
            });

            if (isLatest()) {
                month.value = next;
                monthLoaded.value = true;
            }
        });
    };

    const goToMonth = (anchor: Dayjs): Promise<void> => {
        monthAnchor.value = anchor.startOf('month');

        return refreshMonth();
    };

    const goToArchivePage = (page: number): Promise<void> => {
        archivePage.value = Math.min(Math.max(page, 1), archiveLastPage.value);

        return refreshArchive();
    };

    watch(organizationId, () => {
        archivePage.value = 1;

        refreshActive();
        refreshArchive();
        refreshMonth();
    }, {immediate: true});

    /**
     * Writes reject, unlike the fetches: the caller has a form open and a
     * toast to show, which is exactly what `useLatestFetch` cannot do.
     */
    const requireOrganizationId = (): number => {
        const currentOrganizationId = organizationId.value;

        if (!currentOrganizationId) {
            throw new Error('No organization loaded');
        }

        return currentOrganizationId;
    };

    const afterWrite = async (): Promise<void> => {
        await Promise.all([refreshActive(), refreshArchive(), refreshMonth()]);
        await onWritten?.();
    };

    const create = async (request: AbsenceStoreRequest): Promise<TeamAbsence> => {
        const absence = await teamPlanningApi().createAbsence(requireOrganizationId(), request);

        await afterWrite();

        return absence;
    };

    const update = async (absenceId: number, request: AbsenceUpdateRequest): Promise<TeamAbsence> => {
        const absence = await teamPlanningApi().updateAbsence(absenceId, request);

        await afterWrite();

        return absence;
    };

    const remove = async (absenceId: number): Promise<void> => {
        await teamPlanningApi().deleteAbsence(absenceId);

        await afterWrite();
    };

    return {
        organizationId,
        active: computed<TeamAbsence[]>(() => active.value),
        activeLoading: computed<boolean>(() => activeLoading.value),
        activeFailed: computed<boolean>(() => activeFailed.value),
        activeLoaded: computed<boolean>(() => activeLoaded.value),
        refreshActive,
        archive: computed<TeamAbsence[]>(() => archive.value),
        archiveLoading: computed<boolean>(() => archiveLoading.value),
        archiveFailed: computed<boolean>(() => archiveFailed.value),
        archiveLoaded: computed<boolean>(() => archiveLoaded.value),
        archivePage: computed<number>(() => archivePage.value),
        archiveLastPage: computed<number>(() => archiveLastPage.value),
        archiveTotal: computed<number>(() => archiveTotal.value),
        goToArchivePage,
        refreshArchive,
        month: computed<TeamAbsence[]>(() => month.value),
        monthAnchor: computed<Dayjs>(() => monthAnchor.value),
        monthLoading: computed<boolean>(() => monthLoading.value),
        monthFailed: computed<boolean>(() => monthFailed.value),
        monthLoaded: computed<boolean>(() => monthLoaded.value),
        goToMonth,
        refreshMonth,
        create,
        update,
        remove
    };
};

export default useTeamAbsences;
